<script setup lang="ts">
  import { useFlightStore } from '@/components/fsf/stores/flight.store'
  import { useFsfStore } from '@/components/fsf/stores/fsf.store'
  import { useFsfHelpers } from '@/components/fsf/composables/useFsfHelpers'

  const flightStore = useFlightStore()
  const { isDesktop } = useBTBreakpoints()
  const { showFooterActionBlock } = useFsfHelpers()

  const outboundPrice = computed(
    () => flightStore.selectedOutboundFlight?.updatedPrice ?? flightStore.selectedOutboundFlight?.price
  )
  const inboundPrice = computed(() => flightStore.selectedInboundFlight?.price)

  const fsfStore = useFsfStore()
  const totalPrice = computed(() => {
    let total = outboundPrice.value || 0

    if (fsfStore.isRoundTrip) {
      total += inboundPrice.value || 0
    }
    return total
  })

  const isTotalPriceAvailable = computed(() => {
    if (fsfStore.hideFsfPrices || !fsfStore.showPrices) {
      return false
    }

    if (fsfStore.isRoundTrip) {
      return [outboundPrice.value, inboundPrice.value].every((price) => !!price)
    }

    return !!outboundPrice.value
  })

  const flightsSelected = computed(() => {
    if (fsfStore.isRoundTrip) {
      return !!flightStore.selectedOutboundFlight && !!flightStore.selectedInboundFlight
    }

    return !!flightStore.selectedOutboundFlight
  })

  const { t } = useI18n()
  const priceDisclaimer = computed(() => {
    if (!fsfStore.showPrices) {
      return ''
    }

    return isTotalPriceAvailable.value ? t('fsf.footer.disclaimer.price') : t('fsf.footer.disclaimer.priceUnavailable')
  })

  const submitButton = ref<{ focus: () => void } | null>(null)
  onMounted(() => {
    watch(
      () => flightsSelected.value,
      async (value) => {
        if (value) {
          await nextTick()
          submitButton.value?.focus()
        }
      },
      { immediate: true }
    )
  })
</script>

<template>
  <div class="flex w-full flex-col gap-2 px-4 py-2 md:w-auto md:p-0">
    <div class="flex items-center justify-between md:justify-normal md:gap-8">
      <div class="flex flex-col md:items-end">
        <FsfPrice
          v-if="isTotalPriceAvailable"
          :price="totalPrice"
          size="lg"
        />
        <span
          v-if="isDesktop && flightsSelected"
          class="text-secondary text-body-2"
        >
          {{ priceDisclaimer }}
        </span>
      </div>
      <div>
        <UiButton
          ref="submitButton"
          size="lg"
          class="text-subtitle-1"
          icon="common/airplane-taking-off"
          icon-position="right"
          icon-filled
          icon-size="lg"
          icon-bouncing
          :disabled="!showFooterActionBlock"
          :title="$t('fsf.footer.selectFlight')"
          @click="fsfStore.submitForm"
        />
      </div>
    </div>
    <span
      v-if="!isDesktop"
      class="text-secondary text-body-2"
    >
      {{ priceDisclaimer }}
    </span>
  </div>
</template>
